import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

import { getTeamDeliverables } from '../../../services/deliverableService';

import { sendDeliverableCompletionNotification } from '../../../services/notificationService';
import { supabase } from '../../../lib/supabase';

const StageUploadForm = ({ teamId, teamName }) => {
  const [deliverables, setDeliverables] = useState([]);
  const [selectedDeliverable, setSelectedDeliverable] = useState('');
  const [file, setFile] = useState(null);
  const [description, setDescription] = useState('');
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    if (teamId) {
      loadDeliverables();
    }
  }, [teamId]);

  const loadDeliverables = async () => {
    try {
      setLoading(true);
      const data = await getTeamDeliverables(teamId);
      setDeliverables(data);
    } catch (err) {
      console.error('Error loading deliverables:', err);
      setError(err?.message || 'Failed to load deliverables');
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e) => {
    const selectedFile = e?.target?.files?.[0];
    if (selectedFile) {
      // Validate file size (max 10MB)
      if (selectedFile?.size > 10 * 1024 * 1024) {
        setError('File size must be less than 10MB');
        return;
      }
      setFile(selectedFile);
      setError('');
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setError('');
    setSuccess('');

    if (!selectedDeliverable) {
      setError('Please select a deliverable');
      return;
    }

    if (!file) {
      setError('Please select a file to upload');
      return;
    }

    try {
      setUploading(true);
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      // Generate file path
      const timestamp = Date.now();
      const sanitizedFileName = file?.name?.replace(/[^a-zA-Z0-9.-]/g, '_');
      const filePath = `${teamId}/${selectedDeliverable}/${timestamp}_${sanitizedFileName}`;

      // Upload file to storage
      const { data: uploadData, error: uploadError } = await supabase
        ?.storage
        ?.from('team-stage-files')
        ?.upload(filePath, file);

      if (uploadError) throw uploadError;

      // Create database record
      const { data: uploadRecord, error: dbError } = await supabase
        ?.from('team_deliverable_uploads')
        ?.insert({
          deliverable_id: selectedDeliverable,
          team_id: teamId,
          uploaded_by: user?.id,
          file_name: file?.name,
          file_url: uploadData?.path,
          file_size: file?.size,
          file_type: file?.type,
          description: description || ''
        })
        ?.select()
        ?.single();

      if (dbError) throw dbError;

      // Check if deliverable is now complete
      const deliverable = deliverables?.find(d => d?.id === selectedDeliverable);
      if (deliverable) {
        const { data: uploads } = await supabase
          ?.from('team_deliverable_uploads')
          ?.select('id')
          ?.eq('deliverable_id', selectedDeliverable);

        if (uploads?.length >= deliverable?.requiredUploadCount) {
          // Send completion notification
          await sendDeliverableCompletionNotification({
            teamId: teamId,
            deliverableName: deliverable?.deliverableName,
            teamName: teamName || 'Your Team'
          });
        }
      }

      setSuccess('File uploaded successfully!');
      setFile(null);
      setDescription('');
      setSelectedDeliverable('');
      
      // Reload deliverables to update counts
      await loadDeliverables();

      // Reset file input
      const fileInput = document.getElementById('file-upload');
      if (fileInput) fileInput.value = '';
    } catch (err) {
      console.error('Error uploading file:', err);
      setError(err?.message || 'Failed to upload file');
    } finally {
      setUploading(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center justify-center py-8">
          <Icon name="Loader" size={24} className="animate-spin text-primary" />
          <span className="ml-2 text-muted-foreground">Loading deliverables...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center space-x-2 mb-6">
        <Icon name="Upload" size={24} className="text-primary" />
        <h3 className="text-xl font-semibold text-foreground">Upload Deliverable</h3>
      </div>

      {deliverables?.length === 0 ? (
        <div className="text-center py-8 border-2 border-dashed border-border rounded-lg">
          <Icon name="FileText" size={32} className="mx-auto text-muted-foreground mb-2" />
          <p className="text-muted-foreground">No deliverables available</p>
          <p className="text-sm text-muted-foreground mt-1">Contact your admin to add deliverables</p>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Deliverable Selection */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Select Deliverable <span className="text-error">*</span>
            </label>
            <select
              value={selectedDeliverable}
              onChange={(e) => setSelectedDeliverable(e?.target?.value)}
              className="w-full p-3 rounded-lg border border-border bg-background text-foreground"
              required
            >
              <option value="">Choose a deliverable...</option>
              {deliverables?.map((deliverable) => (
                <option key={deliverable?.id} value={deliverable?.id}>
                  {deliverable?.deliverableName} - Due: {new Date(deliverable?.dueDate)?.toLocaleDateString()} 
                  ({deliverable?.completedUploadCount}/{deliverable?.requiredUploadCount} files)
                </option>
              ))}
            </select>
          </div>

          {/* File Upload */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              File <span className="text-error">*</span>
            </label>
            <input
              id="file-upload"
              type="file"
              onChange={handleFileChange}
              className="w-full p-3 rounded-lg border border-border bg-background text-foreground"
              required
            />
            {file && (
              <p className="text-sm text-muted-foreground mt-2">
                Selected: {file?.name} ({(file?.size / 1024 / 1024)?.toFixed(2)} MB)
              </p>
            )}
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Description (Optional)
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e?.target?.value)}
              placeholder="Add a description for this file..."
              rows="3"
              className="w-full p-3 rounded-lg border border-border bg-background text-foreground resize-y"
            />
          </div>

          {/* Error Message */}
          {error && (
            <div className="p-4 bg-error/10 border border-error/20 rounded-lg">
              <div className="flex items-center space-x-2">
                <Icon name="AlertCircle" size={16} className="text-error" />
                <p className="text-error font-medium">{error}</p>
              </div>
            </div>
          )}

          {/* Success Message */}
          {success && (
            <div className="p-4 bg-success/10 border border-success/20 rounded-lg">
              <div className="flex items-center space-x-2">
                <Icon name="CheckCircle" size={16} className="text-success" />
                <p className="text-success font-medium">{success}</p>
              </div>
            </div>
          )}

          {/* Submit Button */}
          <Button
            type="submit"
            disabled={uploading || !file || !selectedDeliverable}
            className="w-full"
          >
            {uploading ? (
              <>
                <Icon name="Loader" size={16} className="animate-spin mr-2" />
                Uploading...
              </>
            ) : (
              <>
                <Icon name="Upload" size={16} className="mr-2" />
                Upload File
              </>
            )}
          </Button>
        </form>
      )}
    </div>
  );
};

export default StageUploadForm;